import { ReactNode } from "react";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Show
} from "shared/components";
import {
  ArrowRight02Icon,
  ArrowLeft02Icon,
  InformationCircleIcon
} from "@hugeicons-pro/core-solid-rounded";
import { Card } from "../Card";
import { cx } from "shared/utils";
import { HugeiconsIcon } from "@hugeicons/react";

interface TransferListProps<T> {
  remainingItems: T[];
  movingItems: T[];
  originalListTitle: string;
  newListTitle: string;
  itemDetails: (item: T) => ReactNode;
  onMoveToNew: (item: T) => void;
  onMoveBack: (item: T) => void;
  // For getting the key ID used when rendering the items
  getItemId: (item: T) => string;
  originalListErrorMessage?: string;
  newListErrorMessage?: string;
}

interface ListItemProps<T> extends TransferListProps<T> {
  item: T;
  variant: "remaining" | "moving";
}

const ListItem = <T,>(props: ListItemProps<T>) => {
  const { item, itemDetails, onMoveToNew, onMoveBack, variant } = props;
  const transferItem = variant === "remaining" ? onMoveToNew : onMoveBack;
  return (
    <div className="flex gap-1 pl-6 pr-2.5 py-4 justify-between items-center animate-bounce-in">
      {itemDetails(item)}
      <IconButton
        size={IconButtonSize.SM}
        variant={IconButtonVariant.BASE}
        borderLess
        HugeIcon={variant === "remaining" ? ArrowRight02Icon : ArrowLeft02Icon}
        strokeWidth={0.5}
        onClick={() => transferItem(item)}
      />
    </div>
  );
};

export const TransferList = <T,>(props: TransferListProps<T>) => {
  const {
    remainingItems,
    movingItems,
    originalListTitle,
    newListTitle,
    getItemId,
    originalListErrorMessage,
    newListErrorMessage
  } = props;

  return (
    <div className="gap-x-2.5 gap-y-2 min-h-full w-full grid grid-cols-2">
      <div className="flex flex-col space-y-2.5 w-full">
        <p className="text-sm text-content-300">{originalListTitle}</p>
        <Card
          className={cx(
            "flex flex-col gap-1 grow divide-y divide-alpha-40 h-full min-h-[240px]",
            originalListErrorMessage ? "border-danger-400" : ""
          )}
        >
          {remainingItems.map((item) => (
            <ListItem
              key={getItemId(item)}
              variant="remaining"
              item={item}
              {...props}
            />
          ))}
        </Card>
      </div>
      <div className="flex flex-col space-y-2.5 w-full">
        <p className="text-sm text-content-300">{newListTitle}</p>
        <Card
          className={cx(
            "flex flex-col gap-1 grow divide-y divide-alpha-40 h-full min-h-[240px]",
            newListErrorMessage ? "border-danger-400" : ""
          )}
        >
          {movingItems.map((item) => (
            <ListItem
              key={getItemId(item)}
              variant="moving"
              item={item}
              {...props}
            />
          ))}
        </Card>
      </div>
      <Show if={!originalListErrorMessage}>
        {/* For spacing */}
        <div className="h-[20px]" />
      </Show>
      <Show if={originalListErrorMessage}>
        <div className="flex gap-2 items-center h-[20px]">
          <HugeiconsIcon
            icon={InformationCircleIcon}
            className="text-danger-500"
            size="24"
          />

          <p className="text-sm text-danger-500 font-normal w-full">
            {originalListErrorMessage}
          </p>
        </div>
      </Show>
      <Show if={!newListErrorMessage}>
        {/* For spacing */}
        <div className="h-[20px]" />
      </Show>
      <Show if={newListErrorMessage}>
        <div className="flex gap-2 items-center h-[20px]">
          <HugeiconsIcon
            icon={InformationCircleIcon}
            className="text-danger-500"
            size="24"
          />

          <p className="text-sm text-danger-500 font-normal w-full">
            {newListErrorMessage}
          </p>
        </div>
      </Show>
    </div>
  );
};
